import React, { useEffect, useState } from 'react';
import {
  Button, Card, Empty, Skeleton, Space, Table, Tag, Typography, message,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import * as api from '../api/client';
import type { FeatureRequestItem } from '../api/client';

const { Text } = Typography;

const STATUS_TAG_COLORS: Record<string, string> = {
  PENDING_REVIEW: 'orange',
  NEEDS_CLARIFICATION: 'warning',
};

const IMPACT_TAG_COLORS: Record<string, string> = {
  LOW: 'default', MEDIUM: 'blue', HIGH: 'orange', CRITICAL: 'red',
};

const CATEGORY_LABELS: Record<string, string> = {
  AI_AGENTS: 'AI Agents', PLAYBOOKS: 'Playbooks', KNOWLEDGE_BASE: 'Knowledge Base',
  API: 'API', INTEGRATIONS: 'Integrations', DESKTOP_APP: 'Desktop App',
  UI_UX: 'UI / UX', ADMIN: 'Admin', SECURITY: 'Security', BILLING: 'Billing', OTHER: 'Other',
};

const formatDate = (d?: string) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return '—'; }
};

interface Props {
  adminEmail: string;
  onGoToGovernance?: () => void;
}

const AdminPendingList: React.FC<Props> = ({ adminEmail, onGoToGovernance }) => {
  const [items, setItems] = useState<FeatureRequestItem[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!adminEmail) return;
    setLoading(true);
    try {
      const res = await api.getPendingFeatureRequests(adminEmail);
      const all: FeatureRequestItem[] = res.data.items || [];
      setItems(all.filter(i => ['PENDING_REVIEW', 'NEEDS_CLARIFICATION'].includes(i.status)));
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to load pending requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [adminEmail]);

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => <Text strong style={{ fontSize: 13 }}>{title}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (s: string) => (
        <Tag color={STATUS_TAG_COLORS[s] || 'default'} style={{ margin: 0 }}>
          {s === 'PENDING_REVIEW' ? 'Pending Review' : 'Needs Clarification'}
        </Tag>
      ),
    },
    {
      title: 'Submitted by',
      key: 'createdBy',
      width: 160,
      render: (_: unknown, record: FeatureRequestItem) => (
        <Text style={{ fontSize: 12 }}>{record.createdByName || record.createdByUserId}</Text>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 140,
      render: (c: string) => <Text style={{ fontSize: 12 }}>{CATEGORY_LABELS[c] || c}</Text>,
    },
    {
      title: 'Impact',
      dataIndex: 'impactLevel',
      key: 'impactLevel',
      width: 100,
      render: (lvl: string) => (
        <Tag color={IMPACT_TAG_COLORS[lvl] || 'default'} style={{ margin: 0 }}>{lvl}</Tag>
      ),
    },
    {
      title: 'Submitted',
      dataIndex: 'createdOn',
      key: 'createdOn',
      width: 110,
      render: (d: string) => <Text style={{ fontSize: 12 }}>{formatDate(d)}</Text>,
    },
    ...(onGoToGovernance ? [{
      title: '',
      key: 'action',
      width: 110,
      render: () => (
        <Button
          size="small"
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={onGoToGovernance}
        >
          Review
        </Button>
      ),
    }] : []),
  ];

  return (
    <div>
      <Card
        className="praxys-admin-requests-card"
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: '12px 16px' }}
      >
        <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
          <Text style={{ fontSize: 14 }}>
            Submitted feature requests awaiting review
          </Text>
          <Space>
            <Button onClick={() => void load()}>Refresh</Button>
            {onGoToGovernance && (
              <Button type="default" onClick={onGoToGovernance}>
                Open Feature Governance
              </Button>
            )}
          </Space>
        </Space>
      </Card>

      {loading ? (
        <Card className="praxys-admin-requests-card" bodyStyle={{ padding: 20 }}><Skeleton active /></Card>
      ) : items.length === 0 ? (
        <Empty
          description="No pending submissions — all caught up!"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '40px 0' }}
        />
      ) : (
        <Card className="praxys-admin-requests-card" bodyStyle={{ padding: 0 }}>
          <Table
            className="praxys-admin-requests-table"
            dataSource={items}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="middle"
            scroll={{ x: 800 }}
          />
        </Card>
      )}
    </div>
  );
};

export default AdminPendingList;
